import React from 'react';
import Button from 'react-bootstrap/Button';
import Dropdown from 'react-bootstrap/Dropdown';
import Nav from 'react-bootstrap/Nav';
import Navbar from 'react-bootstrap/Navbar';
import { LinkModel } from '../../models/globals';
import { useTouchableDropdown } from '../../hooks/useTouchableDropdown';
import SearchIcon from '../../svgs/search.svg';
import XIcon from '../../svgs/x.svg';
import CartIcon from '../../svgs/carticon.svg';
import { ArrowToggle } from './ArrowToggle';

import { Guid } from 'guid-typescript';
import { debounce, pushAccountDropdownEvent } from '../../services/analytics';

interface AccountAndSearchProps {
    accountLinks: LinkModel[];
    showSearch: boolean;
    showStudentLoginLink: boolean;
    userDisplayName?: string | null;
    studentLoginLink: LinkModel;
    toggleShowSearch(): void;
    shoppingCartCount: number;
    shoppingCartLink: LinkModel;
}

// Debounced hover handler
const handleHover = debounce(() => {
    pushAccountDropdownEvent('Account', window.location.href, Guid.create().toString());
}, 1000); // 1 second

const getUserInitials = (displayName?: string | null): string | null => {
    const nameParts = displayName?.trim().split(/\s+/).filter(Boolean) ?? [];

    if (nameParts.length === 0) {
        return null;
    }

    if (nameParts.length === 1) {
        const initials = nameParts[0].slice(0, 2).toUpperCase();
        return initials.length > 0 ? initials : null;
    }

    return `${nameParts[0].charAt(0)}${nameParts[nameParts.length - 1].charAt(0)}`.toUpperCase();
};

const AccountAndSearch: React.FC<AccountAndSearchProps> = ({
    accountLinks,
    showSearch,
    studentLoginLink,
    showStudentLoginLink,
    userDisplayName,
    toggleShowSearch,
    shoppingCartCount,
    shoppingCartLink,
}) => {
    const showCart = shoppingCartCount > 0;
    const { dropdown: accountDropdown } = useTouchableDropdown();
    const userInitials = getUserInitials(userDisplayName);

    return (
        <Nav className="d-block">
            <Button
                className="px-2"
                onClick={toggleShowSearch}
                title={`${showSearch ? 'Hide' : 'Show'} search`}
                variant="link"
                data-testid="header-search-toggle-button"
            >
                {showSearch ? <XIcon className="x-icon" /> : <SearchIcon />}
            </Button>

            <Navbar.Text>
                {showStudentLoginLink ? (
                    <Nav.Link data-testid="login-button" href={studentLoginLink.url}>
                        {studentLoginLink.name}
                    </Nav.Link>
                ) : (
                    <Dropdown
                        {...accountDropdown}
                        alignRight
                        as={Nav.Item}
                        className="account-nav-dropdown navitem-border-bottom"
                        focusFirstItemOnShow="keyboard"
                        role="toolbar"
                    >
                        <Dropdown.Toggle
                            as={ArrowToggle}
                            id="account-nav-dropdown"
                            onMouseEnter={handleHover}
                            aria-label="Account"
                        >
                            {userInitials && (
                                <span
                                    aria-hidden="true"
                                    className="account-avatar-initials"
                                    data-testid="account-avatar-initials"
                                >
                                    {userInitials}
                                </span>
                            )}
                        </Dropdown.Toggle>

                        <Dropdown.Menu renderOnMount>
                            {accountLinks.map(({ name, url }) => (
                                <Dropdown.Item
                                    active={false}
                                    href={url}
                                    key={name}
                                    onClick={() =>
                                        pushAccountDropdownEvent(
                                            name,
                                            window.location.origin + url,
                                            Guid.create().toString(),
                                        )
                                    }
                                >
                                    <span className="menu-border-bottom">{name}</span>
                                </Dropdown.Item>
                            ))}
                        </Dropdown.Menu>
                    </Dropdown>
                )}
            </Navbar.Text>

            {showCart && (
                <Button
                    href={shoppingCartLink.url}
                    className="pb-0 px-2 position-relative"
                    variant="link"
                    aria-label={`You have ${shoppingCartCount} items in your shopping cart`}
                >
                    <span className="e2g-hosted-cart-ico-counter">{shoppingCartCount}</span>
                    <CartIcon />
                </Button>
            )}
        </Nav>
    );
};

export { AccountAndSearch, AccountAndSearchProps };
